import React, { useEffect, useState, useRef } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MainLayout from "../Layouts/main";
import Pagination from "../components/Pagination";
import { useAuth } from "../context/AuthContext";
import { track } from "@crm/analytics";
import { toast } from "sonner";
import { BlueprintWorkflowModal } from "./BlueprintWorkflowModal";
import BlueprintGrid from "./BlueprintGrid";
import { CategoryTabs, SearchAndSort, VerificationFilter } from "./BlueprintFilters";
import { usePaginationSearch } from "@utils";

function Blueprints() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, interfaceStyle, setShowAuthModal } = useAuth();

    const { currentPage, searchTerm, debouncedSearchTerm, updatePage, updateSearch, clearSearch } =
        usePaginationSearch({ debounceMs: 500 });

    const [category, setCategory] = useState(() => searchParams.get("category") || "all");
    const [sortBy, setSortBy] = useState(() => searchParams.get("sortBy") || "createdAt");
    const [sortOrder, setSortOrder] = useState(() => searchParams.get("sortOrder") || "desc");
    const [badges, setBadges] = useState(() => {
        const raw = searchParams.get("badges");
        return raw ? raw.split(",").filter(Boolean) : [];
    });
    const [showWorkflowModal, setShowWorkflowModal] = useState(false);
    const [selectedBlueprint, setSelectedBlueprint] = useState(null);

    const { call: getBlueprints, response: blueprintsResponse, isLoading: blueprintsLoading } =
        useBluelagoon({ method: "getBlueprints" });
    const { call: getBlueprintById, response: blueprintByIdResponse, error: blueprintByIdError } =
        useBluelagoon({ method: "getBlueprintById" });

    const handleCategoryChange = (val) => {
        setCategory(val);
        updatePage(1);
    };

    const handleSortChange = (value) => {
        const [field, order] = value.split("-");
        setSortBy(field);
        setSortOrder(order);
        updatePage(1);
    };

    const handleBadgesChange = (next) => {
        setBadges(next);
        updatePage(1);
    };

    // Sync category/sort/badges to URL without interfering with usePaginationSearch
    useEffect(() => {
        const p = new URLSearchParams(searchParams);
        let changed = false;
        const setCat = category && category !== "all" ? category : null;
        const setSb = sortBy && sortBy !== "createdAt" ? sortBy : null;
        const setSo = sortOrder && sortOrder !== "desc" ? sortOrder : null;
        const setBd = badges.length > 0 ? badges.join(",") : null;

        if ((p.get("category") || null) !== setCat) {
            if (setCat) p.set("category", setCat); else p.delete("category");
            changed = true;
        }
        if ((p.get("sortBy") || null) !== setSb) {
            if (setSb) p.set("sortBy", setSb); else p.delete("sortBy");
            changed = true;
        }
        if ((p.get("sortOrder") || null) !== setSo) {
            if (setSo) p.set("sortOrder", setSo); else p.delete("sortOrder");
            changed = true;
        }
        if ((p.get("badges") || null) !== setBd) {
            if (setBd) p.set("badges", setBd); else p.delete("badges");
            changed = true;
        }
        if (changed) setSearchParams(p, { replace: true });
    }, [category, sortBy, sortOrder, badges]);

    // Use ref to avoid getBlueprints in dependency array causing loops
    const fetchRef = useRef(null);
    fetchRef.current = () => {
        const params = { page: currentPage, limit: 12, sortBy, sortOrder };
        if (debouncedSearchTerm?.trim()) params.search = debouncedSearchTerm.trim();
        if (category !== "all") params.category = category;
        if (badges.length > 0) params.badges = badges.join(",");
        getBlueprints(params);
    };

    useEffect(() => { fetchRef.current(); }, [currentPage, debouncedSearchTerm, category, sortBy, sortOrder, badges]);

    // Deep-link support
    useEffect(() => {
        const id = searchParams.get("blueprintId");
        if (id && !showWorkflowModal) getBlueprintById({ id });
    }, []);

    useEffect(() => {
        if (blueprintByIdResponse?.status === 200 && blueprintByIdResponse?.data) {
            setSelectedBlueprint(blueprintByIdResponse.data);
            setShowWorkflowModal(true);
        } else if (blueprintByIdError) {
            toast.error(t("blueprints.blueprintNotFound"));
            setSearchParams((p) => { const n = new URLSearchParams(p); n.delete("blueprintId"); return n; });
        }
    }, [blueprintByIdResponse, blueprintByIdError]);

    const handleCreateWorkflow = (blueprint) => {
        if (!user) { setShowAuthModal(true); return; }
        setSelectedBlueprint(blueprint);
        setShowWorkflowModal(true);
        setSearchParams((p) => { const n = new URLSearchParams(p); n.set("blueprintId", blueprint.id); return n; });
    };

    const handleWorkflowCreated = (workflowId, blueprint) => {
        track("Created Workflow from Blueprint", { blueprintId: blueprint.id, blueprintName: blueprint.name });
        toast.success(t("blueprints.workflowCreated", { name: blueprint.name }));
        setTimeout(() => {
            navigate(interfaceStyle === "expert" ? `/workflows/${workflowId}` : `/chat/${workflowId}/chat`);
        }, 100);
    };

    const handleCloseModal = () => {
        setShowWorkflowModal(false);
        setSelectedBlueprint(null);
        setSearchParams((p) => { const n = new URLSearchParams(p); n.delete("blueprintId"); return n; });
    };

    const handleBadgeUpdated = (blueprintId, badges) => {
        if (selectedBlueprint?.id === blueprintId) {
            setSelectedBlueprint((prev) => ({ ...prev, badges }));
        }
        fetchRef.current();
    };

    const blueprints = blueprintsResponse?.data?.blueprints || [];
    const totalPages = blueprintsResponse?.data?.pagination?.totalPages || 0;
    const totalCount = blueprintsResponse?.data?.pagination?.totalCount || 0;

    return (
        <MainLayout>
            <div className="space-y-6">
                <div>
                    <h1 className="page-title">{t("blueprints.title")}</h1>
                    <p className="text-muted-foreground mt-1">{t("blueprints.description")}</p>
                </div>
                <CategoryTabs category={category} onChange={handleCategoryChange} />
                <SearchAndSort
                    searchTerm={searchTerm}
                    onSearchChange={updateSearch}
                    onClearSearch={clearSearch}
                    sortValue={`${sortBy}-${sortOrder}`}
                    onSortChange={handleSortChange}
                />
                <VerificationFilter value={badges} onChange={handleBadgesChange} />
                <BlueprintGrid
                    blueprints={blueprints}
                    loading={blueprintsLoading}
                    hasActiveFilters={!!searchTerm || category !== "all" || badges.length > 0}
                    showFeatured={!searchTerm && category === "all" && badges.length === 0 && currentPage === 1}
                    onCreateWorkflow={handleCreateWorkflow}
                    onClearFilters={() => { clearSearch(); handleCategoryChange("all"); handleBadgesChange([]); }}
                />
                {totalPages > 1 && (
                    <div className="flex justify-center">
                        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={updatePage} itemsPerPage={12} totalItems={totalCount} />
                    </div>
                )}
                <BlueprintWorkflowModal
                    isOpen={showWorkflowModal}
                    onClose={handleCloseModal}
                    onWorkflowCreated={handleWorkflowCreated}
                    blueprint={selectedBlueprint}
                    onBadgeUpdated={handleBadgeUpdated}
                />
            </div>
        </MainLayout>
    );
}

export default Blueprints;
